"use client";

import { InfoCircle } from "iconsax-reactjs";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { PageWrapper } from "@/components/core";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useStatementEligibility } from "@/hooks/statement/useStatementEligibility";
import { MyStatements } from "./MyStatements";
import { NewStatementForm } from "./NewStatementForm";

type Tab = "write" | "mine";

interface StatementTabsProps {
  initialTab?: Tab;
  /** When set, the write tab opens that draft for editing/publishing. */
  draftId?: string;
}

const TONE = {
  alert: { ring: "bg-accent text-accent-foreground", text: "text-accent" },
  success: {
    ring: "bg-semantic-success text-white",
    text: "text-semantic-success",
  },
  error: { ring: "bg-semantic-error text-white", text: "text-semantic-error" },
} as const;

export const StatementTabs: React.FC<StatementTabsProps> = ({
  initialTab = "write",
  draftId,
}) => {
  const t = useTranslations("statement");
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [checked, setChecked] = useState(false);
  const eligibility = useStatementEligibility(checked);

  // The eligibility endpoint is untyped in the OpenAPI spec; cast through unknown
  // to recover the runtime shape, mirroring the pattern in useFetchMyStatements.
  const isEligible = (
    eligibility.data as unknown as { data?: { eligible?: boolean } } | undefined
  )?.data?.eligible;

  const resolved = checked && !eligibility.isLoading;
  const eligibleNow = resolved && isEligible === true;

  // Eligibility gates the form below and drives the note's colour: yellow by
  // default (the requirement), green once eligible, red once known-ineligible.
  const tone: keyof typeof TONE = eligibleNow
    ? "success"
    : resolved && isEligible === false
      ? "error"
      : "alert";
  const message = eligibleNow
    ? t("intro.eligible")
    : resolved && isEligible === false
      ? t("intro.ineligible")
      : t("intro.requirement");

  const onCheck = () => {
    if (!checked) {
      setChecked(true);
    } else {
      eligibility.refetch();
    }
  };

  return (
    <PageWrapper containerClassName="px-4 py-6 md:px-6 lg:px-8 pb-28">
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as Tab)}
        syncWithHash={false}
        className="flex flex-col gap-6"
      >
        <TabsList className="w-full">
          <TabsTrigger value="write" className="flex-1">
            {t("page.tabWrite")}
          </TabsTrigger>
          <TabsTrigger value="mine" className="flex-1">
            {t("page.tabMine")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="write">
          <div className="flex flex-col gap-4">
            {/* Intro — heading + body, on the page background (no card) */}
            <div className="flex flex-col gap-2">
              <h2 className="text-lg font-bold text-text-primary">
                {t("intro.heading")}
              </h2>
              <p className="text-sm leading-relaxed text-text-secondary">
                {t.rich("intro.body", {
                  b: (chunks) => (
                    <b className="font-bold text-text-primary">{chunks}</b>
                  ),
                })}
              </p>
            </div>

            {/* Eligibility note — solid status badge + coloured text + بررسی */}
            <div className="flex items-center gap-3 rounded-2xl bg-bg-secondary px-4 py-3">
              <InfoCircle
                size={20}
                variant="Bold"
                color="currentColor"
                className={`shrink-0 ${TONE[tone].text}`}
                aria-hidden
              />
              <p
                className={`flex-1 text-[10px] font-medium ${TONE[tone].text}`}
              >
                {message}
              </p>
              {!eligibleNow && (
                <Button
                  type="button"
                  variant="solid"
                  colors="default"
                  className="shrink-0 rounded-lg text-[10px]"
                  loading={checked && eligibility.isLoading}
                  onClick={onCheck}
                >
                  {t("intro.checkCta")}
                </Button>
              )}
            </div>

            {/* Form — separate card, same bg as the eligibility note. Locked
                until the balance check confirms the user holds ≥1 DNM. */}
            <div className="rounded-md bg-bg-secondary px-4 py-5">
              <NewStatementForm draftId={draftId} eligible={eligibleNow} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="mine">
          <MyStatements />
        </TabsContent>
      </Tabs>
    </PageWrapper>
  );
};
